<template>
    <CButtonCombined
        :key="activeItem"
        :active="activeItem"
        :items="items"
        :size="size"
        :tooltip="tooltip"
        @submit="submit"
    />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import { EntityType } from '#shared/types/EntityType'
import type { UserProfileType } from '#shared/types/User'
import { CButtonSizes, ButtonVariant } from '@/types/buttons'
import type { CButtonCombinedType } from '@/components/ui/CButtonCombined.vue'

enum PausedRequestsState {
    Active = 'active',
    Inactive = 'inactive'
}

const props = defineProps({
    profileId: {
        type: String,
        required: true
    },
    slug: {
        type: String,
        required: true
    },
    type: {
        type: String as PropType<UserProfileType>,
        required: true
    },
    value: {
        type: Boolean,
        required: true
    },
    size: {
        type: String as PropType<CButtonSizes>,
        default: CButtonSizes.Large
    },
    onSubmit: {
        type: Function,
        required: true
    },
    onChange: {
        type: Function,
        default: () => {}
    }
})

const activeDescription = computed(() => {
    if (props.type === EntityType.Mentor || props.type === 'mentor') {
        return 'Menteemu sa zobrazí tlačidlo "Mám záujem o mentoring"'
    }
    if (props.type === EntityType.Mentee || props.type === 'mentee') {
        return 'Mentorovi sa zobrazí možnosť ťa osloviť'
    }
    return 'Záujemcovi sa zobrazí tlačidlo "Chcem tohto dobrovoľníka"'
})

const inactiveDescription = computed(() => {
    if (props.type === EntityType.Mentor || props.type === 'mentor') {
        return 'Menteemu sa namiesto tlačidla "Mám záujem o mentoring" zobrazí oznam "Teraz neprijíma žiadosti"'
    }
    if (props.type === EntityType.Mentee || props.type === 'mentee') {
        return 'Mentorovi sa zobrazí oznam "Teraz neprijíma žiadosti"'
    }
    return 'Záujemcovi sa namiesto tlačidla "Chcem tohto dobrovoľníka" zobrazí oznam "Teraz neprijíma žiadosti"'
})

const items = computed<CButtonCombinedType[]>(() => [
    {
        id: PausedRequestsState.Active,
        icon: 'check-circle-broken',
        button: {
            variant: 'success-150' as ButtonVariant,
            text: 'Prijímam žiadosti'
        },
        dropdown: {
            text: 'Prijímam žiadosti',
            description: activeDescription.value
        }
    },
    {
        id: PausedRequestsState.Inactive,
        icon: 'refuse',
        button: {
            variant: 'gray-750' as ButtonVariant,
            text: 'Neprijímam žiadosti'
        },
        dropdown: {
            text: 'Neprijímam žiadosti',
            description: inactiveDescription.value
        }
    }
])

const activeItem = computed(() => {
    const value = props.value ? PausedRequestsState.Active : PausedRequestsState.Inactive
    return items.value.findIndex((item) => item.id === value)
})

const tooltip = computed(() => {
    if (props.type === EntityType.Mentor || props.type === 'mentor') {
        return 'Nemá to vplyv na viditeľnosť tvojho mentorského profilu. Po označení "Neprijímam žiadosti" sa menteemu namiesto tlačidla "Mám záujem o mentoring" zobrazí oznam "Teraz neprijíma žiadosti".'
    }
    if (props.type === EntityType.Mentee || props.type === 'mentee') {
        return 'Nemá to vplyv na viditeľnosť tvojho mentee profilu. Po označení "Neprijímam žiadosti" sa mentorovi zobrazí oznam "Teraz neprijíma žiadosti".'
    }
    return 'Nemá to vplyv na viditeľnosť tvojho dobrovoľníckeho profilu. Po označení "Neprijímam žiadosti" sa záujemcovi namiesto tlačidla "Chcem tohto dobrovoľníka" zobrazí oznam "Teraz neprijíma žiadosti".'
})

const submit = (index: number) => {
    const isActive = items.value[index].id === PausedRequestsState.Active
    props.onSubmit(isActive)
}
</script>